'use client'
import CurrencyItem from '@/app/components/common/CurrencyItem'
import Popup from '@/app/components/common/Popup'
import Title from '@/app/components/common/Title'
import TranslateString from '@/app/components/common/TranslateString'
import { Icons } from '@/app/components/ui/Icons'
import {
	DEFAULT_CURRENCY,
	INIT_DATA_STORAGE_KEY,
	LG_BREAKPOINT,
	OTHER_CURRENCIES,
	SM_BREAKPOINT,
	TABLET_DOWN,
	TOP_CURRENCIES,
} from '@/app/constants'
import { Currency, UserData } from '@/app/types'
import GenericCurrencyWhite from '@images/generic-currency-white.svg'
import GenericCurrency from '@images/generic-currency.svg'
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { FC, useEffect, useRef, useState } from 'react'
import { CircleFlag } from 'react-circle-flags'
import { emitCurrencyClickGAEvent } from '../ga-event-helpers'

export interface CurrencySelectorProps {
	isMainPage: boolean
	isSticky: boolean
}

const CurrencySelector: FC<CurrencySelectorProps> = ({
	isMainPage,
	isSticky,
}) => {
	const router = useRouter()
	const pathname = usePathname()
	const query = useSearchParams()
	const params = new URLSearchParams(query)
	const [initData, setItem] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)
	const defaultCurrency = initData?.defaultCurrency || TOP_CURRENCIES[0]
	const userCurrency = initData?.selectedCurrency || defaultCurrency
	const [openCurrencySelect, setOpenCurrencySelect] = useState(false)
	const topCurrenciesRef = useRef<HTMLDivElement>(null)
	const otherCurrenciesRef = useRef<HTMLDivElement>(null)
	const tabletDown = useMediaQuery(TABLET_DOWN)
	const isMobile = useMediaQuery(SM_BREAKPOINT)
	const isDesktop = useMediaQuery(LG_BREAKPOINT)

	// In case user's currency is not in the list, add it to the top and tremove the first item
	let otherCurrencies = [...OTHER_CURRENCIES]

	const defaultCurrencyInOtherList = otherCurrencies.findIndex(
		(currency) => currency.abbreviation === defaultCurrency.abbreviation
	)

	const defaultCurrencyInTopList = TOP_CURRENCIES.findIndex(
		(currency) => currency.abbreviation === defaultCurrency.abbreviation
	)

	if (defaultCurrencyInTopList === -1 && defaultCurrencyInOtherList === -1) {
		otherCurrencies = [{ ...defaultCurrency }, ...otherCurrencies.slice(1)]
	}

	const allCurrencies = [...TOP_CURRENCIES, ...otherCurrencies]
	const currency =
		query.get('currency') || userCurrency.abbreviation || DEFAULT_CURRENCY
	const selectedCurrency = allCurrencies.find(
		(item) => item.abbreviation === currency.toUpperCase()
	)

	const handleCurrencySelect = (currency: string) => {
		emitCurrencyClickGAEvent(currency)

		params.delete('currency')
		params.append('currency', currency)

		if (params.has('mn')) {
			params.delete('mn')
		}
		if (params.has('mx')) {
			params.delete('mx')
		}

		const selectedCurrency = allCurrencies.find(
			(item) => item.abbreviation === currency.toUpperCase()
		) as Currency

		setItem({
			...initData,
			selectedCurrency: selectedCurrency,
		})
		setTimeout(() => {
			router.push(`${pathname}?${params.toString()}`)
		}, 100)

		setOpenCurrencySelect(false)
	}

	const handleOpenCurrencySelect = () => {
		setOpenCurrencySelect(true)
		// emitCurrencySelectClickGAEvent()
	}

	useEffect(() => {
		const body = document.querySelector('body')

		if (tabletDown && openCurrencySelect) {
			body?.classList.add('overflow-hidden')
		}

		return () => {
			body?.classList.remove('overflow-hidden')
		}
	}, [openCurrencySelect, tabletDown])

	useEffect(() => {
		if (isSticky) {
			setOpenCurrencySelect(false)
		}
	}, [isSticky])

	return (
		<div className="currency-selector">
			<div
				className={classNames('currency-selector__button', {
					'justify-center': !selectedCurrency,
					'bg-white/10': openCurrencySelect && isMainPage,
					'bg-text-100': openCurrencySelect && !isMainPage,
					'hover:bg-text-100': !isMainPage,
				})}
				onClick={handleOpenCurrencySelect}
			>
				{selectedCurrency ? (
					<>
						<CircleFlag
							countryCode={selectedCurrency?.flag.toLowerCase() as string}
							className="w-5 h-5 me-2"
						/>
						<span
							className={`text-sm font-semibold ${
								isMainPage ? 'text-white' : 'text-text-900'
							}`}
						>
							{selectedCurrency?.abbreviation}
						</span>
					</>
				) : isMobile || isMainPage ? (
					<GenericCurrencyWhite />
				) : (
					<GenericCurrency />
				)}
			</div>
			<Popup
				isOpen={openCurrencySelect}
				onClose={() => setOpenCurrencySelect(false)}
				className="z-50"
				animationDuration={!isDesktop ? 0 : undefined}
			>
				<div
					className={classNames(
						'currency-selector__currencies-wrapper currency-selector__currencies-wrapper--small-height',
						{
							'border border-text-200': !isMainPage,
						}
					)}
				>
					<div className="flex mb-2 w-full justify-between">
						<Title
							level={4}
							className="text-sm lg:text-2xl text-dark-grey font-bold mb-6 !leading-none"
						>
							<TranslateString i18nKey={'topCurrencies'} context={'Keywords'} />
						</Title>
						<div
							className="currency-selector__close"
							onClick={() => setOpenCurrencySelect(false)}
						>
							<Icons.iconClose />
						</div>
					</div>

					<div className="currency-selector__currencies" ref={topCurrenciesRef}>
						{TOP_CURRENCIES.map((currency) => (
							<CurrencyItem
								key={currency.abbreviation}
								currency={currency}
								onSelect={handleCurrencySelect}
								selected={
									currency.abbreviation === selectedCurrency?.abbreviation
								}
							/>
						))}
					</div>
					<Title level={4} className="text-sm font-bold my-6">
						<TranslateString i18nKey={'otherCurrencies'} context={'Keywords'} />
					</Title>

					<div
						className="currency-selector__currencies"
						ref={otherCurrenciesRef}
					>
						{otherCurrencies.map((currency) => (
							<CurrencyItem
								key={currency.abbreviation}
								currency={currency}
								onSelect={handleCurrencySelect}
								selected={
									currency.abbreviation === selectedCurrency?.abbreviation
								}
							/>
						))}
					</div>
				</div>
			</Popup>
		</div>
	)
}

export default dynamic(() => Promise.resolve(CurrencySelector), {
	ssr: false,
})
